/**
 * adapted from Avatar component at https://github.com/ant-design/ant-design
 */
import classNames from 'classnames';
import React, { Component, PropsWithChildren } from 'react';
import Icon from '../Icon/Icon';
import './style.scss';
import { IAvatarProps } from './types';

export interface IAvatarState {
  scale: number;
  isImgExist: boolean;
}

/**
 * Avatar is adapted from the avatar component at https://github.com/ant-design/ant-design
 */
export class Avatar extends Component<PropsWithChildren<IAvatarProps>, IAvatarState> {
  public static defaultProps = {
    prefixCls: 'hl-avatar',
    shape: 'circle',
    size: 'default',
  };

  public readonly state: Readonly<IAvatarState>;
  private node: HTMLElement | null;
  private avatarChildren: any; // tslint:disable-line

  constructor(props: IAvatarProps) {
    super(props);
    this.state = {
      isImgExist: true,
      scale: 1,
    };
  }

  public componentDidMount() {
    this.setScale();
  }

  public componentDidUpdate(prevProps: IAvatarProps, prevState: IAvatarState) {
    if (
      prevProps.children !== this.props.children ||
      (prevState.scale !== this.state.scale && this.state.scale === 1)
    ) {
      this.setScale();
    }
  }

  public setScale = () => {
    const childrenNode = this.avatarChildren;
    if (childrenNode) {
      const childrenWidth = childrenNode.offsetWidth;

      if (!this.node) {
        return;
      }
      const avatarWidth = this.node.getBoundingClientRect().width;
      // add 4px gap for each side to get better performance
      if (avatarWidth - 8 < childrenWidth) {
        this.setState({
          scale: (avatarWidth - 8) / childrenWidth,
        });
      } else {
        this.setState({
          scale: 1,
        });
      }
    }
  };

  public handleImgLoadError = () => this.setState({ isImgExist: false });

  public render() {
    const { prefixCls, shape, size, src, icon, ...others } = this.props;

    const sizeCls = classNames({
      [`${prefixCls}-lg`]: size === 'large',
      [`${prefixCls}-sm`]: size === 'small',
    });

    /* const classString = classNames(prefixCls, className, sizeCls, {
      [`${prefixCls}-${shape}`]: shape,
      [`${prefixCls}-image`]: src,
      [`${prefixCls}-icon`]: icon,
    }); */

    const classString = classNames(prefixCls, sizeCls, {
      [`${prefixCls}-${shape}`]: !!shape,
      [`${prefixCls}-image`]: !!src,
      [`${prefixCls}-icon`]: !!icon,
    });

    let children = this.props.children;
    if (src && this.state.isImgExist) {
      children = <img src={src} onError={this.handleImgLoadError} />;
    } else if (icon) {
      children = <Icon icon={icon as any} />; //tslint:disable-line
    } else {
      const childrenNode = this.avatarChildren;
      if (childrenNode || this.state.scale !== 1) {
        const childrenStyle = {
          WebkitTransform: `scale(${this.state.scale})`,
          display: 'inline-block',
          left: `calc(50% - ${Math.round(childrenNode.offsetWidth / 2)}px)`,
          position: 'absolute',
          transform: `scale(${this.state.scale})`,
        } as React.CSSProperties;
        children = (
          <span className={`${prefixCls}-string`} ref={(span) => (this.avatarChildren = span)} style={childrenStyle}>
            {children}
          </span>
        );
      } else {
        children = (
          <span className={`${prefixCls}-string`} ref={(span) => (this.avatarChildren = span)}>
            {children}
          </span>
        );
      }
    }
    return (
      <span ref={this.setNodeRef} {...others} className={classString}>
        {children}
      </span>
    );
  }

  public setNodeRef = (ref: HTMLElement | null) => {
    this.node = ref;
  };
}

export default Avatar;
